import { useState, type ReactNode } from 'react';
import { T, catColor, catBg, confidenceColor, CAT_LABELS, MODE_LABELS } from '../lib/tokens';

// ── カテゴリドット ────────────────────────────────────────

export function CatDot({ catId, size = 8 }: { catId: string | null; size?: number }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: 2,
      background: catColor(catId),
      display: 'inline-block', flexShrink: 0,
    }} />
  );
}

// ── カテゴリバッジ ────────────────────────────────────────

export function CatBadge({ catId }: { catId: string | null }) {
  const label = CAT_LABELS[catId ?? ''] ?? catId ?? '未分類';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 7px', borderRadius: 4,
      background: catBg(catId),
      border: `1px solid ${catColor(catId)}40`,
      fontSize: 10, fontFamily: T.fontMono, letterSpacing: '0.04em',
      color: catColor(catId), whiteSpace: 'nowrap',
    }}>
      <CatDot catId={catId} size={5} />
      {label}
    </span>
  );
}

// ── Confidence チップ ─────────────────────────────────────

export function Confidence({ value }: { value: number | null }) {
  if (!value) return null;
  const pct = Math.round(value * 100);
  const color = confidenceColor(value);
  return (
    <span style={{
      fontSize: 10, fontFamily: T.fontMono,
      color, padding: '1px 6px',
      background: `${color}18`,
      border: `1px solid ${color}30`,
      borderRadius: 3, whiteSpace: 'nowrap',
    }}>
      AI {pct}%
    </span>
  );
}

// ── タグチップ ────────────────────────────────────────────

export function TagChip({ name }: { name: string }) {
  return (
    <span style={{
      fontSize: 10, fontFamily: T.fontMono,
      color: T.textDim, padding: '2px 7px',
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid ${T.border}`,
      borderRadius: 3, whiteSpace: 'nowrap',
    }}>
      #{name}
    </span>
  );
}

// ── 大エリア（kind 別表示） ───────────────────────────────
// link  : OGP og:image を表示（失敗時はカラープレースホルダー）
// note  : Gemini 要約を句点・改行で分割した箇条書き
// image : 添付画像を表示（失敗時はプレースホルダー）

// 要約テキストを箇条書き用に分割する
function toBullets(text: string, max = 5): string[] {
  return text
    .split(/\n+|。/)
    .map(s => s.trim().replace(/^[・\-•＊*\s]+/, ''))
    .filter(Boolean)
    .slice(0, max);
}

export function Thumb({
  catId, title, kind, ratio = '16/9', imageUrl = null, summary = '',
}: {
  catId: string | null; title: string; kind: string; ratio?: string;
  imageUrl?: string | null; summary?: string;
}) {
  const [imgError, setImgError] = useState(false);

  const hue = catId === 'memo' || !catId ? 0 : (CAT_THUMB_HUES[catId] ?? 260);
  const bg = hue === 0
    ? 'linear-gradient(135deg, #1a1d24 0%, #12141a 100%)'
    : `linear-gradient(135deg, oklch(0.28 0.06 ${hue}) 0%, oklch(0.18 0.04 ${hue}) 100%)`;

  // note: 箇条書きカード
  if (kind === 'note') {
    const bullets = toBullets(summary || title);
    return (
      <div style={{
        width: '100%', background: bg, borderRadius: 6,
        position: 'relative', overflow: 'hidden',
        border: `1px solid rgba(255,255,255,0.05)`,
        padding: '14px 14px 16px',
      }}>
        <div style={{
          fontSize: 10, fontFamily: T.fontMono, letterSpacing: '0.08em',
          color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 10,
        }}>✎ MEMO</div>
        {bullets.length > 0 ? (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7 }}>
            {bullets.map((b, i) => (
              <li key={i} style={{
                fontSize: 13, lineHeight: 1.5, color: 'rgba(255,255,255,0.82)',
                paddingLeft: 16, position: 'relative',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                <span style={{ position: 'absolute', left: 2, color: catColor(catId) }}>•</span>
                {b}
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>（本文なし）</div>
        )}
      </div>
    );
  }

  // link / image: 画像表示（取得できていれば）
  const showImage = !!imageUrl && !imgError && (kind === 'link' || kind === 'image');
  if (showImage) {
    return (
      <div style={{
        width: '100%', aspectRatio: ratio, background: '#0d0f14', borderRadius: 6,
        position: 'relative', overflow: 'hidden', border: `1px solid rgba(255,255,255,0.05)`,
      }}>
        <img
          src={imageUrl!}
          alt={title}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setImgError(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }

  // フォールバック: カラープレースホルダー
  const kindIcon = { link: '↗', note: '✎', image: '▣', chat: '💬' }[kind] ?? '◇';
  return (
    <div style={{
      width: '100%', aspectRatio: ratio, background: bg, borderRadius: 6,
      position: 'relative', overflow: 'hidden',
      border: `1px solid rgba(255,255,255,0.05)`,
    }}>
      {/* ハッチングパターン */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 8px, rgba(255,255,255,0.018) 8px 9px)',
      }} />
      {/* kind ラベル */}
      <div style={{
        position: 'absolute', top: 8, left: 10,
        fontSize: 10, fontFamily: T.fontMono, letterSpacing: '0.08em',
        color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase',
      }}>{kindIcon} {kind}</div>
      {/* タイトル */}
      <div style={{
        position: 'absolute', bottom: 10, left: 10, right: 10,
        fontSize: 12, lineHeight: 1.4, color: 'rgba(255,255,255,0.7)',
        fontWeight: 500, overflow: 'hidden',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
      }}>{title}</div>
      {/* カテゴリグロー */}
      {hue !== 0 && (
        <div style={{
          position: 'absolute', top: -20, right: -20,
          width: 80, height: 80, borderRadius: '50%',
          background: `oklch(0.72 0.14 ${hue} / 0.15)`,
          filter: 'blur(20px)',
        }} />
      )}
    </div>
  );
}

const CAT_THUMB_HUES: Record<string, number> = {
  tech:210, genai:270, paper:240, news:350, recipe:30, eatery:50,
  global:180, social:160, medical:130, hobby:320, finance:120, life:55, gadget:200,
};

// ── 処理モードバッジ ──────────────────────────────────────

export function ModeBadge({ mode }: { mode: string }) {
  const label = MODE_LABELS[mode] ?? mode;
  const icons: Record<string, string> = {
    save:'📥', research:'🔍', expand:'📖', rewrite:'✏️', custom:'⚙️'
  };
  return (
    <span style={{
      fontSize: 10, fontFamily: T.fontMono, color: T.textMute,
    }}>{icons[mode]}{label}</span>
  );
}

// ── 時刻表示 ─────────────────────────────────────────────

export function TimeAgo({ iso }: { iso: string }) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  let label: string;
  if (mins < 1) label = 'たった今';
  else if (mins < 60) label = `${mins}分前`;
  else if (hours < 24) label = `${hours}時間前`;
  else if (days < 7) label = `${days}日前`;
  else label = d.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });

  return (
    <span style={{ fontSize: 10, fontFamily: T.fontMono, color: T.textMute }}>
      {label}
    </span>
  );
}

// ── ローディング・エラー ──────────────────────────────────

export function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: T.textMute, fontSize: 13 }}>
      <div style={{
        width: 16, height: 16, borderRadius: '50%',
        border: `2px solid ${T.border}`,
        borderTopColor: T.accent,
        animation: 'ml-spin 0.7s linear infinite',
      }} />
      読み込み中...
      <style>{`@keyframes ml-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function ErrorMessage({ message }: { message: string }) {
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 6, fontSize: 12,
      background: T.warnSoft, border: `1px solid ${T.warn}40`, color: T.warn,
    }}>
      ⚠️ {message}
    </div>
  );
}

// ── お気に入り★（トグル） ─────────────────────────────────
// 見た目だけを担当する。実際の更新方法は呼び出し側で変わる:
//   メモ  → ml_items を直接 update
//   記事  → Worker の /api/news-favorite（学習と同期があるので直接更新してはいけない）
// カード内で使う場合は Link へのバブリングを止めるため preventDefault も行う。

const FAV_COLOR = 'oklch(0.82 0.15 85)';

export function FavoriteStar({
  active, onToggle, size = 12, busy = false,
}: { active: boolean; onToggle: () => void; size?: number; busy?: boolean }) {
  return (
    <button
      onClick={e => { e.preventDefault(); e.stopPropagation(); if (!busy) onToggle(); }}
      title={active ? 'お気に入りを解除' : 'お気に入りに追加'}
      aria-pressed={active}
      style={{
        background: 'transparent', border: 'none', padding: 0, lineHeight: 1,
        fontSize: size, color: active ? FAV_COLOR : T.textMute,
        cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
      }}
    >{active ? '★' : '☆'}</button>
  );
}

// ── コピーボタン ──────────────────────────────────────────
// クリップボードにコピーして 1.5 秒だけ「✓」表示に切り替える。
// clipboard API は secure context（HTTPS / localhost）限定なので、失敗時は静かに諦める。

// 「重なった四角」はフォント依存の記号（⧉ / ❐ / ⎘ など）だと 10px 前後で潰れて判別できないため、
// インラインSVGで描く。currentColor なので通常時／コピー完了時の色分けはそのまま効く。
function CopyGlyph({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"
      style={{ display: 'inline-block', verticalAlign: '-1px' }}
    >
      <rect x="5.5" y="5.5" width="9" height="9" rx="1.5" />
      <path d="M10.5 5.5V3a1.5 1.5 0 0 0-1.5-1.5H3A1.5 1.5 0 0 0 1.5 3v6A1.5 1.5 0 0 0 3 10.5h2.5" />
    </svg>
  );
}

export function CopyButton({ text, title = 'コピー' }: { text: string; title?: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // 非 secure context / 権限拒否。ボタンは無反応のままにする。
    }
  }

  return (
    <button
      onClick={copy}
      title={title}
      style={{
        background: 'transparent', border: 'none', padding: 0,
        display: 'inline-flex', alignItems: 'center', gap: 4,
        color: copied ? T.ok : T.textMute,
        fontSize: 10, fontFamily: T.fontMono, cursor: 'pointer', lineHeight: 1,
      }}
    >
      {copied ? <>✓ コピーしました</> : <><CopyGlyph /> コピー</>}
    </button>
  );
}

// ── 軽量 Markdown レンダラー ──────────────────────────────
// 見出し / 箇条書き(- * •) / 番号付き / **太字** / `コード` / [リンク](url) に対応（依存ライブラリなし）

export function MarkdownLite({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0, key = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    // 見出し（# 〜 ####）
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const size = [0, 16, 15, 14, 13][h[1].length] ?? 13;
      blocks.push(
        <div key={key++} style={{ fontSize: size, fontWeight: 700, color: T.text, margin: '8px 0 3px', lineHeight: 1.4 }}>
          {renderInline(h[2])}
        </div>
      );
      i++; continue;
    }

    // 箇条書き（-, *, •）— 先頭インデントで簡易ネスト
    if (/^\s*[-*•]\s+/.test(line)) {
      const items: { indent: number; text: string }[] = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) {
        const m = lines[i].match(/^(\s*)[-*•]\s+(.*)$/)!;
        items.push({ indent: m[1].length, text: m[2] });
        i++;
      }
      blocks.push(
        <ul key={key++} style={{ margin: '3px 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ marginLeft: it.indent >= 2 ? 16 : 0, lineHeight: 1.6 }}>{renderInline(it.text)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // 番号付きリスト（1. 2. …）
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={key++} style={{ margin: '3px 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {items.map((it, idx) => <li key={idx} style={{ lineHeight: 1.6 }}>{renderInline(it)}</li>)}
        </ol>
      );
      continue;
    }

    // 通常の段落
    blocks.push(
      <div key={key++} style={{ margin: '2px 0', lineHeight: 1.7 }}>{renderInline(line)}</div>
    );
    i++;
  }

  return <>{blocks}</>;
}

// インライン記法（**太字** / `コード`）
// [表示テキスト](URL) のうち http/https のみをリンク化する。
// javascript: 等のスキームは LLM 出力・外部由来の本文に混ざりうるので、
// リンクにせず生テキストのまま表示する。
//
// URL 部は括弧を1段だけ許す。Wikipedia の …/X_(engineer) のような URL を
// 最初の ) で切ると、リンクは張られるのに飛び先が違うという事故になるため。
const URL_PART = String.raw`(?:[^()\s]|\([^()\s]*\))+`;
const LINK_RE = new RegExp(String.raw`^\[([^\]]+)\]\((https?://${URL_PART})\)$`);
const INLINE_SPLIT = new RegExp(
  String.raw`(\*\*[^*]+\*\*|\`[^\`]+\`|\[[^\]]+\]\(${URL_PART}\))`, 'g',
);

function renderInline(text: string): ReactNode {
  const parts = text.split(INLINE_SPLIT);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i} style={{ fontWeight: 700 }}>{p.slice(2, -2)}</strong>;
    }
    const link = p.match(LINK_RE);
    if (link) {
      return (
        <a
          key={i} href={link[2]} target="_blank" rel="noopener noreferrer"
          style={{ color: T.accent, textDecoration: 'underline', textUnderlineOffset: 2 }}
        >{link[1]}</a>
      );
    }
    if (p.startsWith('`') && p.endsWith('`')) {
      return (
        <code key={i} style={{
          fontFamily: T.fontMono, fontSize: '0.92em',
          background: 'rgba(255,255,255,0.06)', padding: '1px 4px', borderRadius: 3,
        }}>{p.slice(1, -1)}</code>
      );
    }
    return <span key={i}>{p}</span>;
  });
}
